import {useContext} from "react";
import {format} from "date-fns";
import {I18nReactContext} from "../contexts/I18nContext";
import {SignDocTypes, signDocProgressTypes, isSignDocComplete} from "../Components/SignDoc/SignDocLib";

/**
 * Formats a completion date for display, tolerating a missing or unparsable value.
 *
 * date-fns `format` throws a RangeError on an invalid date and the app has no error boundary,
 * so one bad value would blank the whole page. Returns an empty string instead.
 *
 * @param {object} params - The function parameters container object
 * @param {string|Date|null} params.value - The stored completion date
 * @returns {string} The date as dd/MM/yyyy, or '' when there is nothing valid to show
 */
export const formatCompletionDate = ({value}) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy');
};

/**
 * Converts sign document progress to text representation
 * @param {object} params - The function parameters
 * @param {number} params.progressPercent - Progress percentage (0-100)
 * @param {boolean} params.isSignDocValid - Whether the sign doc is valid
 * @param {string} params.type - Sign document type (from SignDocTypes)
 * @param {boolean} params.viewed - Whether the document was viewed
 * @param {boolean} params.signed - Whether the document was signed
 * @param {number} params.score - Score for exams (used in display)
 * @param {string} params.completedByUserName - Name of admin who force-completed the task
 * @returns {object} Object containing progress text and completion status
 * @returns {string} returns.progress - Localized progress text
 * @returns {boolean} returns.isCompleted - Whether the SignDoc is complete according to new logic
 */
export const useSignDocToTextProgressValueConverter = () => {
    const {i18n} = useContext(I18nReactContext);
    
    return ({progressPercent, isSignDocValid, type, viewed, signed, score, completedByUserName}) => {
        let progress, isCompleted;
        if (type === SignDocTypes.REGULAR_COURSE) {
            if (isSignDocValid && (progressPercent === 0 || !progressPercent)) {
                progress = i18n.translate('courseProgressMgmnt.notStarted')
                isCompleted = false
            } else if (progressPercent > 0 && progressPercent < 100) {
                progress = i18n.translate('courseProgressMgmnt.inProgress')
                isCompleted = false
            } else if (isSignDocComplete({ type, progressPercent, viewed, signed })) {
                progress = i18n.translate('courseProgressMgmnt.finished')
                isCompleted = true
            }
        } else if (type === SignDocTypes.EXAM) {
            if (isSignDocValid && (progressPercent === 0 || !progressPercent)) {
                progress = i18n.translate('courseProgressMgmnt.notStarted')
                isCompleted = false
            } else if (progressPercent > 0 && progressPercent < 100) {
                progress = i18n.translate('courseProgressMgmnt.inProgress')
                isCompleted = false
            } else if (isSignDocComplete({ type, progressPercent, viewed, signed })) {
                // For completed exams, we'll show the score in the UserRowSignDocsMenu component
                progress = completedByUserName ? i18n.translate('courseProgressMgmnt.finished') : `${i18n.translate('courseProgressMgmnt.score')}: ${parseInt(score)}`
                isCompleted = true
            }
        } else if (type === SignDocTypes.LEARNING_PATH) {
            if (isSignDocValid && (progressPercent === 0 || !progressPercent)) {
                progress = i18n.translate('courseProgressMgmnt.notStarted')
                isCompleted = false
            } else if (progressPercent > 0 && progressPercent < 100) {
                // Show progress percentage with label
                progress = i18n.translate('learningPath.progressPercent') + ' ' + progressPercent + '%'
                isCompleted = false
            } else if (isSignDocComplete({ type, progressPercent, viewed, signed })) {
                progress = i18n.translate('courseProgressMgmnt.finished')
                isCompleted = true
            }
        } else {
            if (isSignDocValid && !viewed && !signed) {
                progress = i18n.translate('signDocProgressMgmnt.notStarted')
                isCompleted = false
            } else if (viewed && !signed) {
                progress = i18n.translate('signDocProgressMgmnt.inProgress')
                isCompleted = false
            } else if (signed && type !== SignDocTypes.LINK) {
                progress = i18n.translate('signDocProgressMgmnt.finished')
                isCompleted = true
            } else if (signed && type === SignDocTypes.LINK) {
                progress = i18n.translate('signDocProgressMgmnt.opened')
                isCompleted = true
            }
        }
        return {
            progress,
            isCompleted
        }
    }
}

/**
 * Converts sign document progress to CSS class representation
 * @param {object} params - The function parameters
 * @param {boolean} params.signed - Whether the document was signed
 * @param {boolean} params.viewed - Whether the document was viewed
 * @param {number} params.progressPercent - Progress percentage
 * @param {string} params.type - Sign document type
 * @returns {string} CSS class name for progress styling
 */
export const signDocToClassProgressValueConverter = ({signed, viewed, progressPercent, type}) => {
    if (isSignDocComplete({type, progressPercent, viewed, signed})) {
        return "completed"
    } else if (signDocProgressTypes.includes(type)) {
        if (progressPercent > 0 && progressPercent < 100) {
            return "inProgress"
        } else {
            return "notStarted"
        }
    } else {
        if (viewed) {
            return "inProgress"
        } else {
            return "notStarted"
        }
    }
}

/**
 * Gets sign document date text with proper formatting
 * @param {object} params - The function parameters
 * @param {string} params.finishByDate - Finish by date
 * @param {string} params.completedOnDate - Completed on date
 * @param {boolean} params.viewed - Whether the document was viewed
 * @param {boolean} params.signed - Whether the document was signed
 * @param {string} params.type - Sign document type
 * @param {number} params.progressPercent - Progress percentage
 * @param {string} params.completedByUserName - Name of user who completed
 * @returns {string|undefined} Formatted date text or undefined
 */
export const useGetSignDocDateText = () => {
    const {i18n} = useContext(I18nReactContext);
    
    return  ({
        finishByDate,
        completedOnDate,
        viewed,
        signed,
        type,
        progressPercent,
        completedByUserName}) => {
        const dateOptions = {day: '2-digit', month: '2-digit', year: 'numeric'};
        let signDocDate = new Date(completedOnDate).toLocaleDateString('en-GB', dateOptions)
        let signDocDateText;
        
        const isComplete = isSignDocComplete({type, progressPercent, viewed, signed});
        
        if (signDocProgressTypes.includes(type)) {
            if (isComplete) {
                if (!completedOnDate) {
                    return undefined
                } else {
                    if (completedByUserName) {
                        signDocDateText = i18n.translate('courseProgressMgmnt.by') + completedByUserName
                    } else {
                        signDocDateText = i18n.translate('courseProgressMgmnt.completedOnCourse') + signDocDate
                    }
                }
            } else if (progressPercent < 100) {
                if (!finishByDate) {
                    return undefined
                } else {
                    if (completedByUserName) {
                        signDocDateText = i18n.translate('courseProgressMgmnt.by') + completedByUserName
                    } else {
                        signDocDateText = i18n.translate('courseProgressMgmnt.finishByCourse') + signDocDate
                    }
                }

            } else {
                return undefined
            }
        } else {
            if (isComplete) {
                if (!completedOnDate) {
                    return undefined
                } else {
                    if (completedByUserName) {
                        signDocDateText = i18n.translate('courseProgressMgmnt.by') + completedByUserName
                    }
                    else{
                        signDocDateText = i18n.translate('courseProgressMgmnt.completedOn') + signDocDate
                    }
                }
            } else if (viewed) {
                if (!finishByDate) {
                    return undefined
                } else {
                    if (completedByUserName) {
                        signDocDateText = i18n.translate('courseProgressMgmnt.by') + completedByUserName
                    }
                    else{
                        signDocDateText = i18n.translate('courseProgressMgmnt.finishBy') + signDocDate
                    }
                }
            } else {
                return undefined
            }
        }
        return signDocDateText
    }
}
